import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { fetchAirportMeta, isMxIcao, mxLookupCache } from "@/lib/newTripConstants";

// Enriches each leg with Mexican customs + immigration hours (AIP SENEAM)
// whenever the leg is an INTERNATIONAL flight touching a Mexican airport —
// i.e. one endpoint is "MM…" and the other endpoint is a defined, non-MX
// ICAO. Unlike US CBP (arrivals only), Mexican customs is processed on BOTH
// arrival and departure, so the card surfaces for either:
//   * dest = MX, origin = non-MX  → arrival into Mexico   (role "destination")
//   * origin = MX, dest = non-MX  → departure from Mexico (role "origin")
// A domestic MX leg (MM… → MM…) is suppressed: both ends share customs space.
//
// Mirrors useCbpEnrichment's debounce + per-ICAO cache + abort discipline.
// On a hit we attach `mx_customs_info` (the AIP MX record plus `role` and the
// airport's `tz_offset` so the card can render hours in local time). On a 404
// we cache `null` so the lookup never repeats for that ICAO.
export default function useMxCustomsEnrichment(legs, setLegs) {
  const mxDepKey = useMemo(
    () =>
      legs
        .map(
          (l) =>
            `${(l.origin || "").trim().toUpperCase()}>${(l.destination || "").trim().toUpperCase()}`
        )
        .join("|"),
    [legs]
  );

  // Debounce keystrokes the same way the CBP effect does (250ms = typical
  // "stopped typing" pause) so we don't fire a SENEAM lookup per character.
  const [debouncedKey, setDebouncedKey] = useState(mxDepKey);
  useEffect(() => {
    const id = setTimeout(() => setDebouncedKey(mxDepKey), 250);
    return () => clearTimeout(id);
  }, [mxDepKey]);

  useEffect(() => {
    const controllers = [];
    legs.forEach((leg, idx) => {
      const origin = (leg.origin || "").trim().toUpperCase();
      const dest = (leg.destination || "").trim().toUpperCase();
      const originValid = /^[A-Z]{4}$/.test(origin);
      const destValid = /^[A-Z]{4}$/.test(dest);

      // Pick the Mexican endpoint whose OTHER endpoint is a defined, non-MX
      // ICAO (i.e. an international crossing). At most one endpoint can be MX
      // on an international leg, so there's never an ambiguity to resolve.
      let icao = null;
      let role = null;
      if (destValid && isMxIcao(dest) && originValid && !isMxIcao(origin)) {
        icao = dest;
        role = "destination";
      } else if (originValid && isMxIcao(origin) && destValid && !isMxIcao(dest)) {
        icao = origin;
        role = "origin";
      }

      // Not an international MX leg → wipe any stale card so it doesn't linger.
      if (!icao) {
        if (leg.mx_customs_info || leg._mx_checked) {
          setLegs((prev) => {
            const next = [...prev];
            if (next[idx]) next[idx] = { ...next[idx], mx_customs_info: null, _mx_checked: null };
            return next;
          });
        }
        return;
      }

      // Already resolved this ICAO for this leg in the same role — nothing to do.
      if (leg._mx_checked === `${icao}:${role}`) return;

      // Apply synchronously from cache when we've seen this ICAO before.
      if (mxLookupCache.has(icao)) {
        const cached = mxLookupCache.get(icao);
        setLegs((prev) => {
          const next = [...prev];
          if (next[idx]) {
            next[idx] = {
              ...next[idx],
              _mx_checked: `${icao}:${role}`,
              mx_customs_info: cached ? { ...cached, role } : null,
            };
          }
          return next;
        });
        return;
      }

      const controller = new AbortController();
      controllers.push(controller);
      // Fetch the AIP MX record and the airport tz_offset in parallel; the
      // offset lets the card show customs hours in the airport's local time
      // (the AIP publishes them in UTC).
      Promise.all([
        api.get(`/aip-mx/airports/${icao}`, { signal: controller.signal }),
        fetchAirportMeta(icao),
      ])
        .then(([res, meta]) => {
          const record = { ...res.data, tz_offset: meta?.tz_offset ?? null };
          mxLookupCache.set(icao, record);
          setLegs((prev) => {
            const next = [...prev];
            if (next[idx]) {
              next[idx] = {
                ...next[idx],
                _mx_checked: `${icao}:${role}`,
                mx_customs_info: { ...record, role },
              };
            }
            return next;
          });
        })
        .catch((err) => {
          if (controller.signal.aborted) return;
          // Mirrors useCbpEnrichment: a 404 means "no AIP MX record here" and
          // is cacheable; anything else (500, timeout, offline) is unknown.
          // Either way, clear the stale record — it belongs to the previous
          // airport and would show its customs hours on this leg.
          const isDefinitive = err.response?.status === 404;
          if (isDefinitive) mxLookupCache.set(icao, null);
          setLegs((prev) => {
            const next = [...prev];
            if (next[idx]) {
              next[idx] = {
                ...next[idx],
                ...(isDefinitive ? { _mx_checked: `${icao}:${role}` } : {}),
                mx_customs_info: null,
              };
            }
            return next;
          });
        });
    });
    return () => controllers.forEach((c) => c.abort());
    // `legs` is intentionally read stale; mxDepKey captures the only changes
    // (origin/destination strings) that should re-run the lookup.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedKey]);
}
